import type { BoxProps } from '@palmetto/palmetto-components';

// Shared between the Underwriting settings display card and the edit modal so the
// co-applicant Min FICO tooltip copy, validation, and styling stay in sync.
export const MULTI_APPLICANT_MIN_FICO_TOOLTIP =
  'Optional. When a co-applicant is present, this replaces the standard Min FICO Score for everyone on the deal — only one applicant must meet it. Must be 300–850 and at least the standard Min FICO Score.';

export const CO_APPLICANT_MIN_FICO_FLOOR = 300;
export const CO_APPLICANT_MIN_FICO_CEILING = 850;

/**
 * Validates the optional co-applicant Min FICO field. Blank disables the adjustment.
 * When set it must be a whole number in [300, 850] and at least the standard Min FICO Score.
 * Returns `true` when valid, otherwise an error message string (react-hook-form `validate` shape).
 */
export const validateCoApplicantMinFico = (
  rawValue: number | string | undefined | null,
  standardMinFico: number | string | undefined | null,
): true | string => {
  const raw = String(rawValue ?? '').trim();
  if (raw === '') return true;

  const value = Number(raw);
  if (!Number.isInteger(value)) return 'Must be a whole number';
  if (value < CO_APPLICANT_MIN_FICO_FLOOR || value > CO_APPLICANT_MIN_FICO_CEILING) {
    return `Must be between ${CO_APPLICANT_MIN_FICO_FLOOR} and ${CO_APPLICANT_MIN_FICO_CEILING}`;
  }

  const standard = Number(standardMinFico);
  if (Number.isFinite(standard) && value < standard) {
    return 'Must be ≥ Min FICO Score';
  }

  return true;
};

export const CREDIT_OPTIMIZATION_FICO_FLOOR = 300;
export const CREDIT_OPTIMIZATION_FICO_CEILING = 850;

export const CREDIT_OPTIMIZATION_TOOLTIP =
  'Optional. When the first bureau’s FICO falls within this range (inclusive), the waterfall pulls the next bureau to seek a higher score — even if the first score already passes approval. Leave both blank to disable. Each bound must be a whole number 300–850, and Max must be ≥ Min.';

export const CREDIT_OPTIMIZATION_BOTH_OR_NEITHER_ERROR = 'Set both optimization bounds, or leave both blank';

/**
 * Validates one bound of the optional credit optimization range. Blank disables optimization,
 * but only when BOTH bounds are blank — a half-configured range is rejected so the user can't
 * save just one bound. When set, the bound must be a whole number in [300, 850]. When both bounds
 * are set, the upper bound (`creditOptimizationMax`) must be ≥ the lower bound (`creditOptimizationMin`).
 * Returns `true` when valid, otherwise an error message string (react-hook-form `validate` shape).
 *
 * @param rawValue   the value of the field being validated
 * @param otherBound the value of the sibling bound (read live via `getValues`)
 * @param isMax      whether the field being validated is the upper bound
 */
export const validateCreditOptimizationRange = (
  rawValue: number | string | undefined | null,
  otherBound: number | string | undefined | null,
  isMax: boolean,
): true | string => {
  const raw = String(rawValue ?? '').trim();
  const otherRaw = String(otherBound ?? '').trim();

  if (raw === '') {
    // Both blank disables optimization; exactly one blank is a half-configured range.
    return otherRaw === '' ? true : CREDIT_OPTIMIZATION_BOTH_OR_NEITHER_ERROR;
  }
  if (otherRaw === '') {
    return CREDIT_OPTIMIZATION_BOTH_OR_NEITHER_ERROR;
  }

  const value = Number(raw);
  if (!Number.isInteger(value)) return 'Must be a whole number';
  if (value < CREDIT_OPTIMIZATION_FICO_FLOOR || value > CREDIT_OPTIMIZATION_FICO_CEILING) {
    return `Must be between ${CREDIT_OPTIMIZATION_FICO_FLOOR} and ${CREDIT_OPTIMIZATION_FICO_CEILING}`;
  }

  const otherValue = Number(otherRaw);
  if (Number.isFinite(otherValue)) {
    if (isMax && value < otherValue) {
      return 'Max must be ≥ Min';
    }
    if (!isMax && value > otherValue) {
      return 'Min must be ≤ Max';
    }
  }

  return true;
};

export const noop = () => {};

export const tooltipTriggerStyle = { display: 'inline-flex', alignItems: 'center', cursor: 'help' } as const;
export const tooltipContentStyle = { width: '300px' } as const;
// Consumed by HoverOnlyTooltipComponent as raw CSS (spread into a `style={...}`, not Box props),
// so these are genuine CSS values (e.g. '14px') rather than BoxProps design tokens.
export const tooltipContainerStyle = { padding: '0.75em', fontSize: '14px', width: 'auto' } as unknown as BoxProps;
